import type { FlowNodeEntity } from '@flowgram.ai/free-layout-editor';
import type { FlowNodeData } from '$/domains/workFlowV3/type';
import type { PreviewMediaItem } from '$/pages/RoboneoChatBoard/type';
import { resolveVideoCoverUrl } from '$/utils/upload';
import { handleImageUrl } from '$/utils/image';
import type { VideoNodePayload } from './types';
import { markCanvasCacheableUrl } from '../../utils/canvasImageCache';

/** 视频节点无媒体、有连入输入时的占位尺寸（对齐 Figma 38249:9486） */
export const VIDEO_INPUT_PLACEHOLDER_SIZE = {
  width: 360,
  height: 240,
} as const;

/** 视频封面加载失败时的统一兜底图 */
export const VIDEO_COVER_FALLBACK_URL = 'https://roboneo-public.meitudata.com/public/6a796b53697523254c4ojvUzCh1904.svg';

/**
 * 封面 img 加载失败时切换到兜底图，已是兜底图则不再替换，避免 onError 死循环。
 */
export function handleVideoCoverImgError(event: { currentTarget: HTMLImageElement; }) {
  const img = event.currentTarget;
  if (img.dataset.coverFallback === '1') return;
  img.dataset.coverFallback = '1';
  img.src = VIDEO_COVER_FALLBACK_URL;
}

const VIDEO_SHORT_SIDE = 240;
const DEFAULT_VIDEO_SIZE = { width: 360, height: 240 };

/**
 * 根据视频宽高计算画布内展示尺寸。
 */
export function computeVideoNodeSize(
  video: { width?: number; height?: number; } | undefined,
): { width: number; height: number; } {
  if (!video?.width || !video?.height) return DEFAULT_VIDEO_SIZE;
  if (video.width === video.height) {
    return { width: VIDEO_SHORT_SIDE, height: VIDEO_SHORT_SIDE };
  }
  if (video.width >= video.height) {
    return {
      width: Math.round((VIDEO_SHORT_SIDE * video.width) / video.height),
      height: VIDEO_SHORT_SIDE,
    };
  }
  return {
    width: VIDEO_SHORT_SIDE,
    height: Math.round((VIDEO_SHORT_SIDE * video.height) / video.width),
  };
}

/**
 * 判断视频节点是否存在上游连入（连线或 inputNodeId 记录）。
 */
export function hasVideoNodeInputConnection(
  node: FlowNodeEntity,
  data?: FlowNodeData | null,
): boolean {
  const hasLineInput = (node.lines.inputNodes?.length ?? 0) > 0;
  const inputNodeId = data?.inputNodeId;
  const hasInputNodeId = Boolean(
    inputNodeId
    && Object.values(inputNodeId).some((ids) => Array.isArray(ids) && ids.length > 0),
  );
  return hasLineInput || hasInputNodeId;
}

/**
 * 按国内/海外解析视频节点画布展示用的播放地址与封面图。
 * 播放地址优先使用压缩视频；海外额外优先压缩水印视频。
 * 封面图规则不变：国内优先水印封面，海外使用无水印封面。
 *
 * @param media - 视频节点 media_list 首项
 * @param options - isOversea 为 true 时走海外资源
 */
export function resolveVideoNodeDisplayUrls(
  media: VideoNodePayload | undefined,
  options?: { isOversea?: boolean; },
): { playbackUrl: string; coverUrl: string; } {
  if (!media) {
    return { playbackUrl: '', coverUrl: '' };
  }

  const isOversea = options?.isOversea ?? false;
  const playbackUrl = isOversea
    ? (media.compress_url || media.url || media.watermark_compress_url || media.watermark_url || '')
    : (media.watermark_compress_url || media.compress_url || media.watermark_url || media.url || '');
  const coverUrlBase = resolveVideoCoverUrl(media.cover_url, media.url);
  const watermarkCoverUrl = resolveVideoCoverUrl(media.watermark_cover_url, media.watermark_url);
  const coverUrl = isOversea
    ? coverUrlBase
    : (watermarkCoverUrl || coverUrlBase || resolveVideoCoverUrl(undefined, media.watermark_url || media.url) || '');

  return { playbackUrl, coverUrl };
}

/** 将 Team Flow 视频封面统一转换为 webp 并标记为画布媒体缓存请求。 */
export function getCanvasVideoCoverUrl(coverUrl: string): string {
  return markCanvasCacheableUrl(handleImageUrl(coverUrl));
}

/**
 * 根据视频节点媒体数据构建全屏预览项。
 * 缩略图使用封面图（cover_url / watermark_cover_url 或 vframe 截帧），对齐旧版 TeamNodeVideo 展开逻辑。
 *
 * @param media - 视频节点 media_list 首项
 * @param options - isOversea 控制播放地址与缩略图是否走水印资源，name 透传节点展示名用于下载
 */
export function buildVideoNodePreviewMediaItem(
  media: VideoNodePayload | undefined,
  options?: { isOversea?: boolean; name?: string; },
): PreviewMediaItem | null {
  if (!media?.url && !media?.watermark_url && !media?.compress_url && !media?.watermark_compress_url) {
    return null;
  }

  const isOversea = options?.isOversea ?? false;
  const { playbackUrl, coverUrl: cover } = resolveVideoNodeDisplayUrls(media, { isOversea });
  const watermarkCoverUrl = resolveVideoCoverUrl(media.watermark_cover_url, media.watermark_url);

  return {
    url: playbackUrl,
    watermarkUrl: media.watermark_url,
    cover,
    watermarkCoverUrl: watermarkCoverUrl || undefined,
    type: 'video',
    isUserRole: false,
    task_id: media.task_id || '',
    name: options?.name,
    extra: {
      media_url: media.url,
      media_url_by_water_mark: media.watermark_url,
      media_url_by_compress: media.compress_url,
      media_url_by_water_mark_compress: media.watermark_compress_url,
    },
  };
}
